import { useEffect, useState } from 'react';
import { normalizeUser } from '@/services/supabase';

import { useAppTheme } from '@/context/ThemeContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card_Widget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, PieChart } from '@/components/charts';
import { business } from '@/services/review.service';
import type { BusinessReviewStats } from '@/services/review.service';
import { mockBusinessReviewStats } from '@/utils/mockData';
import ExportButtons from '@/components/ExportButtons';

export default function RatingsDashboard() {
  // Get current user from Supabase on component mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        // Get current user directly from Supabase
        const { createClient } = await import('@/services/supabase');
        const supabase = createClient();
        const { data: { session } } = await (await supabase).auth.getSession();
        
        // Check if we have a valid session
        if (!session?.user) {
          console.error('No valid Supabase session found');
          return;
        }
        
        // Use normalizeUser to create a consistent user object
        console.debug('Using normalizeUser in RatingsDashboard getCurrentUser');
        const normalizedUser = normalizeUser(session.user);
        
        // Get user role and business_id from normalized user
        const userRole = normalizedUser.role || 'business';
        const businessId = normalizedUser.business_id;
        
        // Keep original implementation for backward compatibility
        
        // Check if user has proper permissions
        const isBusiness = userRole === 'business';
        const isSuperAdmin = userRole === 'super_admin';
        
        if (!isBusiness && !isSuperAdmin) {
          console.error('User does not have business or super_admin role');
          return;
        }
        
        // Fetch data with the user information
        fetchData(session.user.id, businessId);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    
    getCurrentUser();
  }, []);
  const { themeConfig } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<BusinessReviewStats | null>(null);
  const [processedStats, setProcessedStats] = useState({
    positiveReviews: 0,
    negativeReviews: 0,
    categoryRatings: {
      'Timely Delivery': 0,
      'Sufficient Quantity': 0,
      'Easy Collection': 0
    }
  });

  useEffect(() => {
    const fetchReviewStats = async () => {
      // Check if user is super admin
      // @ts-expect-error - custom properties on user object
      const isSuperAdmin = user?.user_metadata?.role === 'super_admin' || user?.metadata?.role === 'super_admin';
      
      // User may have business_id as extended property
      // @ts-expect-error - custom properties on user object
      const businessId = user?.business_id || user?.metadata?.business_id;
      
      // Default business ID for super admin if none is found
      const effectiveBusinessId = isSuperAdmin ? '8c3e08e8-f9b0-4cd8-a55a-49435583f001' : businessId;
      
      if (!effectiveBusinessId) {
        setError('No business ID found');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        let reviewStats: BusinessReviewStats;
        
        if (isSuperAdmin) {
          // For super admin, always use mock data
          console.log('Using mock data for super admin');
          
          // Get business mock data by ID or use the default one
          reviewStats = mockBusinessReviewStats[effectiveBusinessId];
          if (!reviewStats) {
            console.log('Using default mock business review stats');
            reviewStats = mockBusinessReviewStats['8c3e08e8-f9b0-4cd8-a55a-49435583f001'];
          }
          
          // Short timeout to simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          // For regular business users, use real API with proper error handling
          try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Request timed out')), 10000); // 10 second timeout
              setTimeout(() => reject(new Error('Request timed out after 5000ms')), 5000);
            });
            
            // Race between the actual API call and the timeout
            reviewStats = await Promise.race([
              business.reviews.getStats(effectiveBusinessId),
              timeoutPromise
            ]) as BusinessReviewStats;
            
            // If we got an empty response or no reviews, throw an error
            if (!reviewStats || reviewStats.total_reviews === 0) {
              throw new Error('No reviews found for this business');
            }
          } catch (apiError) {
            console.error('Error fetching business reviews:', apiError);
            throw apiError instanceof Error 
              ? apiError 
              : new Error('Failed to load review data');
          }
        }
        
        setStats(reviewStats);
        
        // Process the data for display
        const positive = reviewStats.total_reviews > 0 
          ? Math.round((reviewStats.rating_distribution['4'] + reviewStats.rating_distribution['5']) || 0)
          : 0;
        const negative = reviewStats.total_reviews > 0
          ? Math.round((reviewStats.rating_distribution['1'] + reviewStats.rating_distribution['2'] + reviewStats.rating_distribution['3']) || 0) 
          : 0;
          
        setProcessedStats({
          positiveReviews: positive,
          negativeReviews: negative,
          categoryRatings: {
            'Timely Delivery': reviewStats.timely_delivery_percent,
            'Sufficient Quantity': reviewStats.sufficient_quantity_percent,
            'Easy Collection': reviewStats.easy_collection_percent
          }
        });
      } catch (error: any) {
        console.error('Error fetching review stats:', error);
        setError(error.message || 'Failed to fetch review statistics');
        // Set default empty stats on error
        setStats({
          average_rating: 0,
          total_reviews: 0,
          rating_distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
          timely_delivery_percent: 0,
          sufficient_quantity_percent: 0,
          easy_collection_percent: 0,
          recent_comments: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReviewStats();
  },[] );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-6">
            <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
          </div>
          <div className="bg-white shadow-md rounded-lg p-6">
            <div className="text-center">
              <div className="mb-4">
                <svg className="h-16 w-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-500 mb-6">{error}</p>
              <p className="text-gray-500">This could be due to a connection timeout or because there are no reviews yet.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-6">
            <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <p className="text-gray-600 mb-4">No ratings data available for this business</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
            </div>
            
            {stats && (
              <ExportButtons 
                data={stats}
                title="Business Ratings Report"
                subtitle="Business Performance Overview"
                type="business-review"
                filename="business-ratings-report"
              />
            )}
          </div>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Average Rating</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.average_rating.toFixed(1)}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Total Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.total_reviews}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Positive Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{processedStats.positiveReviews}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Negative Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{processedStats.negativeReviews}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="distribution" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Rating Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart
                    data={[
                      { name: '5 Stars', value: stats.rating_distribution['5'] || 0 },
                      { name: '4 Stars', value: stats.rating_distribution['4'] || 0 },
                      { name: '3 Stars', value: stats.rating_distribution['3'] || 0 },
                      { name: '2 Stars', value: stats.rating_distribution['2'] || 0 },
                      { name: '1 Star', value: stats.rating_distribution['1'] || 0 }
                    ]}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Review Sentiment</CardTitle>
                </CardHeader>
                <CardContent>
                  <PieChart
                    data={[
                      { name: 'Positive', value: processedStats.positiveReviews },
                      { name: 'Negative', value: processedStats.negativeReviews }
                    ]}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <Card className="bg-white shadow-md">
              <CardHeader>
                <CardTitle style={{ color: themeConfig.primary }}>Quality Metrics (%)</CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={[
                    { name: 'Timely Delivery', value: processedStats.categoryRatings['Timely Delivery'] },
                    { name: 'Sufficient Quantity', value: processedStats.categoryRatings['Sufficient Quantity'] },
                    { name: 'Easy Collection', value: processedStats.categoryRatings['Easy Collection'] }
                  ]}
                />
              </CardContent>
            </Card>
            {stats.recent_comments.length > 0 && (
              <Card className="bg-white shadow-md mt-6">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Recent Comments</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {stats.recent_comments.map((comment, index) => (
                      <li key={index} className="border-b pb-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-3">
                            <div className="bg-blue-100 text-blue-700 font-bold rounded-full w-10 h-10 flex items-center justify-center">
                              {comment.rating}
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-700">{comment.comment}</p>
                            <p className="text-xs text-gray-500 mt-1">{new Date(comment.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
} 

function fetchData(_id: string, __businessId: any) {
  throw new Error('Function not implemented.');
}
